// Depot.tsx
import React, { useEffect, useState } from 'react';
import { Snackbar } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import Mock from '../../constants/Mock';
import { IDepot, IDepotCreatePayload } from '../../interfaces/Depot.interface';
import { IFormData, IFormSchema, IInitialValues, IOptions } from '../../interfaces/Form-Modal.interface';
import { IDepotsDataTable, IColumnTable } from '../../interfaces/Table.interface';
import AddEditModal from '../../shared/components/add-edit-modal/Add-Edit-Modal';
import Loader from '../../shared/components/Loader/Loader';
import Stats from '../../shared/components/stats/Stats';
import { createDepot, deleteDepot, getDepotList } from '../../services/Depot.service';
import { useTranslation } from 'react-i18next';
import ButtonComponent from '../../shared/components/button/Button';
import IconExport from './../../assets/icons/icon-export.svg';
import IconCross from './../../assets/icons/icon-cross.svg';
import IconStatusRejected from './../../assets/icons/icon-status-rejected.svg';
import * as Yup from 'yup';
import { useSnackbar } from '../../services/SnackbarUtils';
import { IPagination } from '../../interfaces/Pagination.interface';
import { ExportData } from '../../services/Export.service';
import ConfirmDialog from '../../shared/components/confirm-dialog/Confirm-Dialog';

interface IDepotsProps {
  CountriesOptions: IOptions[];
}

const formSchema: IFormSchema = Yup.object({
  name: Yup.string().required(),
  country: Yup.object().required(),
});

const formInitialValues: IInitialValues = {
  name: '',
  country: '',
};

const Depots = (props: IDepotsProps): JSX.Element => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();

  // Query Params
  const searchParams = new URLSearchParams(location.search);
  const countryFilters = searchParams.get('countryFilters')?.split(',').map((item) => item.trim());

  // UI State
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteConfirmModal, setOpenDeleteConfirmModal] = useState(false);
  const [isRequestInProgress, setIsRequestInProgress] = useState<boolean>();

  // Snackbar
  const { snackBarOpen, snackbarMessage, snackBarType, showSnackbar, handleSnackBarClose } =
    useSnackbar();

  // Depots State
  const [depotsSearchValue, setDepotsSearchValue] = useState('');
  const [depotsDataTable, setDepotsDataTable] = useState<IDepotsDataTable[]>([]);
  const [depotsListPagination, setDepotsListPagination] = useState<IPagination>();
  const [selectedDepotId, setSelectedDepotId] = useState<string | null | undefined>(null);
  const [exportUrl, setExportUrl] = useState<string>();

  // Form Data
  const { DepotsModalForm } = Mock();
  const [DepotForm, setDepotForm] = useState<IFormData[]>(
    JSON.parse(JSON.stringify(DepotsModalForm))
  );

  // Table Columns
  const columnsTable: IColumnTable[] = [
    { accessorKey: 'name', header: t('depot_name') },
    { accessorKey: 'country', header: t('country') },
    { accessorKey: 'status', header: t('status') },
    { accessorKey: 'action', header: t('action'), 
      Cell: ({ row }: any) => {
        return <div className="status-container">
          <ButtonComponent
            iconUrl={IconStatusRejected}
            iconAlt="icon-delete"
            iconOnly={true}
            iconRound={true}
            label="Delete"
            color="default"
            onClick={() => {
              handleDeleteDepot(row.original.id);
            }}
          />
          </div>;
      }, 
    },
  ];

  const handleDeleteDepot = (depotId?: string): void => {
    setSelectedDepotId(depotId);
    setOpenDeleteConfirmModal(true);
  };

  const handleConfirmDeleteDepot = (): void => {
    if (selectedDepotId === undefined || selectedDepotId === null) {
      setOpenDeleteConfirmModal(false);
      return;
    }

    setIsRequestInProgress(true);
    deleteDepot(selectedDepotId)
      .then(() => {
        showSnackbar(t('depot_has_been_deleted_successfully'), 'success');
        fetchDepotsList(0);
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
    setOpenDeleteConfirmModal(false);
  };

  const fetchDepotsList = (offset: number): void => {
    setIsRequestInProgress(true);
    getDepotList(10, offset, countryFilters, depotsSearchValue)
      .then((depotsList) => {
        setIsRequestInProgress(false);
        setDepotsListPagination(depotsList.pagination);
        getDepotsDataTable(depotsList.data);
        setExportUrl(depotsList.exportUrl);
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
  };

  const handleCreateDepot = (formData: IFormSchema): void => {
    setIsRequestInProgress(true);
    const depotPayload: IDepotCreatePayload = {
      name: formData.name,
      country: formData.country.value,
      status: 'active',
    };
    createDepot(depotPayload)
      .then(() => {
        setIsRequestInProgress(false);
        setOpenModal(false);
        showSnackbar(t('depot_has_been_created_successfully'), 'success');
        fetchDepotsList(0);
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
  };

  const handleTablePaginationChange = (tablePageIndex: number): void => {
    const offset = tablePageIndex * 10;
    fetchDepotsList(offset);
  };

  const DepotsExport = (): any => {
    if (exportUrl !== undefined) {
      setIsRequestInProgress(true);

      ExportData(exportUrl)
        .then(() => {
          setIsRequestInProgress(false);
        })
        .catch((error) => {
          setIsRequestInProgress(false);
          showSnackbar(error.message, 'error');
        });
    }
  };

  const getDepotsDataTable = (depotsData: IDepot[]): void => {
    const depotsDataTable: IDepotsDataTable[] = depotsData.map((depot) => ({
      id: depot.id,
      name: depot.name,
      country: depot.country,
      status: depot.status,
    }));

    setDepotsDataTable(depotsDataTable);
  };

  const handleSubmitSearch = (value: string): void => {
    setDepotsSearchValue(value);
  };

  const searchHandleClear = (): void => {
    setDepotsSearchValue('');
  };

  const openAddDepotModal = (): void => {
    setOpenModal(true);
  };

  const modalClose = (): void => {
    setOpenModal(false);
  };

  const depotsRowClicked = (depotId: string): void => {
    navigate(`/depots/details/${depotId}`);
  };

  const SetModalForm = async (): Promise<void> => {
    const formData = DepotForm;
    
    // Find the "country" field in the Form array
    const countryFieldIndex = formData.findIndex((field) => field.name === 'country');
    if (countryFieldIndex !== -1) {
      // Fill The options By the first default options values
      formData[countryFieldIndex].options = props.CountriesOptions;
    }

    setDepotForm(formData);
  };

  useEffect(() => {
    fetchDepotsList(0);
    void SetModalForm();
  }, [props, location, depotsSearchValue]);

  /* Method That Displays Buttons On Header's page */
  const setDepotsHeaderButtons = (): React.JSX.Element => {
    return (
      <>
        {exportUrl !== undefined && (
          <ButtonComponent
            iconUrl={IconExport}
            iconAlt="icon-export"
            label={t('export')}
            color="secondary btn-transparent btn-outlined"
            onClick={DepotsExport}
          />
        )}

        <ButtonComponent
          iconUrl={IconCross}
          iconAlt="icon-cross"
          label={t('add_depot')}
          color="primary"
          onClick={openAddDepotModal}
        />
      </>
    );
  };
  
  return (
    <div>
      {isRequestInProgress === true && <Loader />}

      <Stats
        searchValue={depotsSearchValue}
        onSearchSubmit={handleSubmitSearch}
        onSearchClear={searchHandleClear}
        headerButtons={setDepotsHeaderButtons()}
        dataTable={depotsDataTable}
        columnsTable={columnsTable}
        emptyMsgTable={isRequestInProgress === false ? t('no_depots_to_display') : ''}
        onTableRowClicked={depotsRowClicked}
        handleTablePaginationChange={handleTablePaginationChange}
        tableTotalPages={depotsListPagination !== undefined ? depotsListPagination.totalPages : 0}
        searchPlaceholder={t('depot_search_placeholder')}
      />

      <Snackbar
        className={`oryx-snackbar ${snackBarType}`}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackBarOpen}
        message={snackbarMessage}
        autoHideDuration={3000}
        onClose={handleSnackBarClose}

      />
      
      <AddEditModal
        modalTitle={t('add_a_depot')}
        modalForm={DepotForm}
        formSchema={formSchema}
        formInitialValues={formInitialValues}
        openModal={openModal}
        onClose={modalClose}
        handleSubmit={handleCreateDepot}
        closeConfirmationModalTitle={t('are_you_sure')}
        closeConfirmationModalDescription={t('depot_info_will_be_lost')}
      />

      <ConfirmDialog
        openModal={openDeleteConfirmModal}
        title={'Delete Confirmation'}
        description={'Are you sure you want to delete this depot?'}
        confirmLabel={'Delete depot'}
        cancelLabel={t('cancel')}
        confirmButtonColor={'inactive'}
        handleConfirm={handleConfirmDeleteDepot}
        handleClose={() => {
          setOpenDeleteConfirmModal(false);
        }}
      />

    </div>
  );
};

export default Depots;
